import analyticsInstance from 'libs/rtzr-commons/modules/marketing/Analytics';
import { useCallback, useEffect, useRef } from 'react';

const THRESHOLD = 0.6; // 섹션이 얼마나 보여야 체크할지 비율, 기본값: 0.6 (60%)
const MIN_DWELL_TIME = 1000; // 최소 체류 시간, 기본값: 1000ms (1초)

export function useAttentionTracker({ sectionName }: { sectionName: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const isVisibleRef = useRef(false); // 섹션이 화면에 보이는지 여부
  const entryTimeRef = useRef<number | null>(null); // 섹션이 화면에 들어온 시간
  const totalDwellTimeRef = useRef(0); // 섹션의 총 체류 시간
  const hasLoggedRef = useRef(false); // 로깅 중복 방지용

  const logDwellTime = useCallback(() => {
    if (hasLoggedRef.current) {
      return;
    }
    // 컴포넌트 언마운트 시 최종 체류 시간 기록 (현재 보이는 시간 포함해서 누적값 계산)
    const finalTotal =
      entryTimeRef.current && isVisibleRef.current
        ? totalDwellTimeRef.current + (Date.now() - entryTimeRef.current)
        : totalDwellTimeRef.current;

    if (finalTotal < MIN_DWELL_TIME) {
      return;
    }

    hasLoggedRef.current = true;
    analyticsInstance.logEvent('section_total_engagement', {
      section_name: sectionName,
      total_dwell_time_seconds: Math.round(finalTotal / 1000),
    });
  }, [sectionName]);

  useEffect(() => {
    hasLoggedRef.current = false;
    let observer: IntersectionObserver | null = null;

    if (ref.current) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // 섹션이 화면에 들어왔을 때
            if (entry.isIntersecting && !isVisibleRef.current) {
              isVisibleRef.current = true;
              entryTimeRef.current = Date.now();
            }
            // 섹션이 화면에서 들어왔다가 나갔을 때
            else if (!entry.isIntersecting && entryTimeRef.current && isVisibleRef.current) {
              isVisibleRef.current = false;
              const dwellTime = Date.now() - entryTimeRef.current;
              totalDwellTimeRef.current += dwellTime;
              entryTimeRef.current = null;
            }
          });
        },
        { threshold: THRESHOLD }
      );

      observer.observe(ref.current);
    }

    window.addEventListener('beforeunload', logDwellTime);

    return () => {
      if (observer) {
        observer.disconnect();
      }

      window.removeEventListener('beforeunload', logDwellTime);
      logDwellTime();
    };
  }, [logDwellTime]);

  return { ref };
}
